/**
 * @engine/input — Keyboard Device
 *
 * Keyboard input device with all standard keys.
 */

import type { KeyCode } from '../types';
import { InputDevice } from './InputDevice';
import { ButtonControl } from '../controls/ButtonControl';

/**
 * Standard keyboard device.
 * Creates button controls for all standard keys on demand.
 */
export class Keyboard extends InputDevice {
  /** Any key pressed this frame */
  readonly anyKey: ButtonControl;

  /** Currently pressed key codes */
  private _pressedKeys = new Set<KeyCode>();

  constructor(index: number = 0) {
    super('keyboard', index, 'Keyboard');
    this.anyKey = new ButtonControl('anyKey');
    this.registerControl(this.anyKey);
  }

  // ============================================================================
  // Key Access
  // ============================================================================

  /**
   * Get or create a key control.
   * Keys are created lazily to avoid creating 100+ controls upfront.
   */
  getKey(code: KeyCode): ButtonControl {
    let control = this.controls.get(code) as ButtonControl | undefined;
    if (!control) {
      control = new ButtonControl(code);
      this.registerControl(control);
    }
    return control;
  }

  /**
   * Check if a key is pressed
   */
  isKeyPressed(code: KeyCode): boolean {
    return this.getKey(code).isPressed;
  }

  /**
   * Check if a key was just pressed this frame
   */
  wasKeyPressed(code: KeyCode): boolean {
    return this.getKey(code).wasPressed;
  }

  /**
   * Check if a key was just released this frame
   */
  wasKeyReleased(code: KeyCode): boolean {
    return this.getKey(code).wasReleased;
  }

  // ============================================================================
  // Modifier Access
  // ============================================================================

  get shiftPressed(): boolean {
    return this.getKey('ShiftLeft').isPressed || this.getKey('ShiftRight').isPressed;
  }

  get ctrlPressed(): boolean {
    return this.getKey('ControlLeft').isPressed || this.getKey('ControlRight').isPressed;
  }

  get altPressed(): boolean {
    return this.getKey('AltLeft').isPressed || this.getKey('AltRight').isPressed;
  }

  get metaPressed(): boolean {
    return this.getKey('MetaLeft').isPressed || this.getKey('MetaRight').isPressed;
  }

  // ============================================================================
  // State Updates
  // ============================================================================

  /**
   * Update key state from backend event
   */
  setKeyState(code: KeyCode, pressed: boolean, time: number): void {
    const key = this.getKey(code);
    key.update(pressed, time);

    if (pressed) {
      this._pressedKeys.add(code);
    } else {
      this._pressedKeys.delete(code);
    }

    // Update anyKey
    this.anyKey.update(this._pressedKeys.size > 0, time);
  }

  /**
   * Get all currently pressed keys
   */
  getPressedKeys(): ReadonlySet<KeyCode> {
    return this._pressedKeys;
  }

  override reset(): void {
    super.reset();
    this._pressedKeys.clear();
    this.anyKey.reset();
  }

  // ============================================================================
  // Control path resolution
  // ============================================================================

  override getControl(name: string): ButtonControl | undefined {
    // For keyboard, any unknown control is assumed to be a key code
    if (name === 'anyKey') return this.anyKey;
    return this.getKey(name as KeyCode);
  }
}
